import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, apiClient, supportPath } from '@/lib/api-client';
import { bugReportKeys } from '@/hooks/use-bug-reports';
import type { BugReportDetail, BugReportStatus, SupportMessage } from '@/types/bug-reports';

/** GET /admin/support/bug-reports/:reference — PRD-0013 R3. */
export function useBugReportDetail(
  reference: string | undefined
): UseQueryResult<BugReportDetail, ApiError> {
  return useQuery({
    queryKey: ['bug-reports', 'detail', reference],
    queryFn: ({ signal }) =>
      apiClient.get<BugReportDetail>(
        supportPath(`/bug-reports/${encodeURIComponent(reference as string)}`),
        signal
      ),
    enabled: reference !== undefined,
    staleTime: 10_000,
  });
}

/** POST .../bug-reports/:reference/messages — the admin's side of the conversation (R5). */
export function useAdminReplyMessage(): UseMutationResult<
  SupportMessage,
  ApiError,
  { reference: string; body: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reference, body }) =>
      apiClient.post<SupportMessage>(
        supportPath(`/bug-reports/${encodeURIComponent(reference)}/messages`),
        { body }
      ),
    onSuccess: (_message, { reference }) => {
      void queryClient.invalidateQueries({ queryKey: ['bug-reports', 'detail', reference] });
    },
  });
}

/** PUT .../bug-reports/:reference/status — open/in_progress/resolved (R4). */
export function useUpdateTicketStatus(): UseMutationResult<
  void,
  ApiError,
  { reference: string; status: BugReportStatus }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reference, status }) =>
      apiClient.put<void>(
        supportPath(`/bug-reports/${encodeURIComponent(reference)}/status`),
        { status }
      ),
    onSuccess: (_void, { reference }) => {
      void queryClient.invalidateQueries({ queryKey: ['bug-reports', 'detail', reference] });
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.all });
    },
  });
}
